<template>
  <div class="order-table-container small">
    <b-alert show variant="info" class="text-center">All pricing being provided during the inquiry are estimates.
      Final pricing will be provided once a Sales or Customer Service contacts you confirming your order.
      Inquiries not converted to an Order in 30 days will be canceled.
    </b-alert>
    <b-tabs
      class="mb-3"
      nav-class="filter-tabs"
      fill
      v-model="tabIndex"
      :end="true"
      @input="onInquiryStatusTabInput"
    >
      <b-tab
        v-for="(status, index) in statuses"
        :key="index"
        :title="status"
      >
      </b-tab>
    </b-tabs>
    <div class="my-2">
      <b-navbar type="light" variant="light" class="actions-bar">
        <b-navbar-nav class="ml-1">
          <b-input-group>
            <b-input-group-prepend><b-input-group-text class="search-icon"><b-icon icon="search"></b-icon></b-input-group-text></b-input-group-prepend>
            <b-form-input class="mr-sm-2 search-bar" placeholder="Sales Inquiry Id / Customer / Job Name" v-model.trim="searchTerm" debounce="500" :class="{
              'is-invalid': searchTerm.length > 0 && searchTerm.length < 1,
            }"></b-form-input>
            <div v-if="searchTerm.length > 0 && searchTerm.length < 1" class="invalid-feedback">
              Search requires minimum of 1 character
            </div>
          </b-input-group>
        </b-navbar-nav>
        <b-navbar-nav v-if="(isFiltered || isPreFiltered)" class="hide-for-mobile">
          <b-nav-item>
            <b-button variant="outline-primary" class="primary-outline-btn" @click="clearFilters" data-cy="reset-filters">
              <b-icon icon="x-circle"></b-icon>
              <b class="ml-2">Reset Filter</b>
            </b-button>
          </b-nav-item>
        </b-navbar-nav>
        <b-navbar-nav class="reset-filter-icon">
          <b-nav-item>
            <b-button variant="flat" class="primary-outline-btn p-1 color-primary" @click="filterModalVisible = true">
              <b-icon :icon="(isFiltered || isPreFiltered) ? 'filter-circle-fill' : 'filter-circle'" @click="filterModalVisible = true"></b-icon>
            </b-button>
          </b-nav-item>
        </b-navbar-nav>
        <b-navbar-nav class="ml-auto">
          <b-nav-item>
            <b-button
              type="button"
              variant="outline-primary"
              class="mr-3"
              @click.prevent="openCrossReferencePdf"
            >
              <b-icon icon="file-earmark-pdf" class="mr-1"></b-icon>
              <b>Cross Reference</b>
            </b-button>
            <b-button variant="info" class="background-color-primary" data-cy="create-inquiry-btn" @click="onNewInquiry">
              <b-icon icon="plus"></b-icon>
              <b class="ml-2 new-inquiry-text">Create Inquiry</b>
            </b-button>
          </b-nav-item>
        </b-navbar-nav>
      </b-navbar>
    </div>
    <div
      style="display: flex; align-items: center; gap: 10px; font-size: medium"
      v-if="isFiltered"
    >
      Filtered By:
      <b-badge
        v-for="(value, key) in activeFilters"
        variant="warning"
        pill
        :key="'filter-badge' + key"
        href="#"
        @click="filter[key] = ''"
        >{{ value }} <b-icon icon="x-circle-fill" class="ml-1"></b-icon
      ></b-badge>
    </div>
    <b-table :items="inquiries.List" :fields="fields" responsive hover borderless class="order-table" :busy="inquiries.loading" thead-class="th-custom-class"
>
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle primary-color mr-2"></b-spinner>
          <strong class="primary-color">Loading...</strong>
        </div>
      </template>
      <template v-slot:head(details)>
        <div></div>
      </template>
      <template v-slot:cell(details)="row">
        <b-icon :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'" @click="row.toggleDetails">
        </b-icon>
      </template>
      <template v-slot:cell(InquiryId)="{ item }">
        <router-link
          :to="{
            name: 'inquiry_summary',
            params: { inquiryId: item.InquiryId + '' },
          }"
          class="order-link"
          @click.native="rememberInquiryListTabForBack"
        >
          {{ item.InquiryId }}
        </router-link>
      </template>
      <template v-slot:cell(Customer)="{ item }">
        <div class="d-flex align-items-center">
          <b-icon icon="shop" variant="info" class="mr-2" />
          <div>
            <router-link
              :to="'/customer/'+item.CustomerNumber"
              class="order-link"
            >
              {{ item.CustomerName }}
            </router-link>
            <div>{{ item.BillToParty.Address1 }}</div>
            <div>
              {{ item.BillToParty.City }}, {{ item.BillToParty.State }}
              {{ item.BillToParty.Zip }}
            </div>
          </div>
        </div>
      </template>
      <template v-for="field in fields.filter((f) => f.isSortable)" v-slot:[`head(${field.key})`]="{ }">
        <div :key="field.key">
          <div class="d-flex align-items-center">
            <div class="d-flex align-items-center">
              {{ field.label }}
              <b-dropdown dropright no-caret size="sm" variant="link" boundary="window" block>
                <template #button-content>
                  <b-icon
                    v-if="field.isFilterable"
                    :icon="
                      filter[field.key] !== ''
                        ? 'filter-circle-fill'
                        : 'filter-circle'
                    "
                    :variant="filter[field.key] !== '' ? 'primary' : 'secondary'"
                    class="header-icon inquiry-filters"
                    :id="'filter-' + field.key"
                  ></b-icon>
                </template>
                <div class="px-2">
                  <label class="d-flex align-items-center small">
                    <template>
                      {{ field.label }}
                    </template>
                    <template v-if="field.isSortable">
                      <b-icon
                        v-if="sortOptions.by === field.key"
                        @click="sort(field.key)"
                        :icon="sortOptions.desc ? 'sort-down' : 'sort-up'"
                        class="header-icon ml-2 order-filters"
                      ></b-icon>
                      <b-icon
                        v-else
                        @click="sort(field.key)"
                        :icon="'sort-down'"
                        class="header-icon ml-2 order-filters"
                        style="color: #000 !important"
                      ></b-icon>
                    </template>
                  </label>
                  <b-input-group size="sm" class="mt-2">
                    <b-input-group-prepend
                      v-if="field.isDate"
                    >
                      <b-form-datepicker
                        v-model.trim="filter[field.key]"
                        button-only
                        size="sm"
                        locale="en-US"
                        button-variant="link"
                      ></b-form-datepicker>
                    </b-input-group-prepend>
                    <b-form-input
                      :id="'filter' + field.key"
                      v-if="field.isDate"
                      v-model.trim="filter[field.key]"
                      type="text"
                      size="sm"
                      placeholder="YYYY-MM-DD"
                      autocomplete="off"
                      debounce="1000"
                    ></b-form-input>
                    <b-form-input
                      :id="'filter' + field.key"
                      v-else
                      type="text"
                      :placeholder="'Enter ' + field.label"
                      v-model.trim="filter[field.key]"
                      debounce="1000"
                    ></b-form-input>
                    <b-input-group-append v-if="filter[field.key].length > 0"
                      ><b-input-group-text
                        class="clear-btn"
                        @click="filter[field.key] = ''"
                        ><b-icon icon="x"></b-icon></b-input-group-text
                    ></b-input-group-append>
                  </b-input-group>
                </div>
              </b-dropdown>
              <template v-if="field.isSortable">
                <b-icon
                  v-if="sortOptions.by === field.key"
                  @click="sort(field.key)"
                  :icon="sortOptions.desc ? 'sort-down' : 'sort-up'"
                  class="header-icon ml-2 order-filters"
                ></b-icon>
              </template>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:cell(Material)="{ item }">
        <div class="align-items-center">
          <div>
            <div class="font-weight-bold">
              {{ item.Items.length }} items(s)
            </div>
            <div v-for="i in item.Items.slice(0, 2)" :key="i.ItemNumber">
              <div class="row mt-1">
                <div class="col-sm-3 text-wrap text-break">
                  {{ i.MaterialNumber }}
                </div>
                <div class="col-sm-7 text-wrap text-break">
                  {{ i.Description }}
                </div>
                <div class="col-sm-2 text-wrap text-break">
                  {{ i.Quantity }}
                </div>
              </div>
            </div>
          </div>
          <div class="" v-if="item.Items.length > 2"><b>+More</b></div>
        </div>
      </template>
      <template v-slot:cell(ShipVia)="{ item }">
        <div class="d-flex align-items-center" v-if="item.Deliveries != null && item.Deliveries.length > 0">
          <b-icon icon="truck" variant="info" class="mr-2" />
          <div>
            <a href="#" v-for="delivery in item.Deliveries" :key="delivery.DeliveryId">{{ delivery.Carrier }}</a>
          </div>
        </div>
        <div class="d-flex align-items-center" v-else-if="item.ExpectedShippingDate">
          <p title="Expected Shipping Date">
            {{ formatDate(item.ExpectedShippingDate) }}
          </p>
        </div>
      </template>
      <template #row-details="row">
        <b-card>
          <b-row class="mb-2">
            <b-col cols="4" class="row-details-label">Customer:</b-col>
            <b-col cols="8">
              <div class="text-left">
                <div>
                  <div>{{ row.item.CustomerName }}</div>
                  <div>{{ row.item.BillToParty.Address1 }}</div>
                  <div>
                    {{ row.item.BillToParty.City }},
                    {{ row.item.BillToParty.State }}
                    {{ row.item.BillToParty.Zip }}
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="4" class="row-details-label">Material <br />({{ row.item.Items.length }} items):</b-col>
            <b-col cols="8">
              <div class="text-left">
                <div>
                  <div v-for="i in row.item.Items.slice(0, 2)" :key="i.ItemNumber">
                    <div class="text-wrap text-break">
                      <b class="color-primary">{{ i.Quantity }}</b> of
                      {{ i.MaterialNumber }} ({{ i.Description }})
                    </div>
                  </div>
                </div>
                <div class="" v-if="row.item.Items.length > 2">
                  <b>+More</b>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="4" class="row-details-label">Total Price</b-col>
            <b-col cols="8 text-left">
              {{ formatPrice(row.item.Total) }}
            </b-col>
          </b-row>
          <b-button size="sm" class="mt-2 w-100" @click="row.toggleDetails" variant="outline"><b-icon icon="chevron-double-up" variant="link" /></b-button>
        </b-card>
      </template>
    </b-table>
    <div class="d-flex justify-content-center" md="my-5">
      <b-pagination v-if="inquiries.Page > 0" v-model="inquiries.Page" :total-rows="inquiries.Total" :per-page="inquiries.PageSize" limit="10" @change="(page) => pageChanged(page)">
      </b-pagination>
    </div>
    <b-modal v-model="filterModalVisible" title="Filter Data" centered size="xl" footer-class="justify-content-center">
      <b-container fluid>
        <b-row>
          <!-- Loop through filterable fields to generate columns -->
          <b-col v-for="field in fields.filter((f) => f.isFilterable)" :key="field.key" cols="12">
            <div class="mb-3">
              <label :for="field.key" class="small">{{ field.label }}</label>
              <b-icon v-if="sortOptions.by === field.key" @click="sort(field.key)" :icon="sortOptions.desc ? 'sort-down' : 'sort-up'" class="header-icon ml-2"></b-icon>
              <b-icon v-else @click="sort(field.key)" :icon="'sort-down'" class="header-icon ml-2" style="color: lightgray !important"></b-icon>
              <b-input-group size="text-sm">
                <b-input-group-prepend v-if="field.isDate">
                  <b-form-datepicker v-model.trim="filter[field.key]" button-only size="sm" locale="en-US" button-variant="link"></b-form-datepicker>
                </b-input-group-prepend>
                <b-form-input v-if="field.isDate" v-model.trim="filter[field.key]" type="text" size="sm" placeholder="YYYY-MM-DD" autocomplete="off" debounce="1000"></b-form-input>
                <b-form-input v-else type="text" :placeholder="'Filter ' + field.label" v-model.trim="filter[field.key]" debounce="1000"></b-form-input>
                <b-input-group-append v-if="filter[field.key].length > 0"><b-input-group-text class="clear-btn" @click="filter[field.key] = ''"><b-icon icon="x"></b-icon></b-input-group-text></b-input-group-append>
              </b-input-group>
            </div>
          </b-col>
        </b-row>
      </b-container>
      <div slot="modal-footer" class="filter-modal-footer">
        <div class="text-center my-2" v-if="inquiries.loading">
          <b-spinner class="align-middle primary-color mr-2"></b-spinner>
          <strong class="primary-color">Filtering...</strong>
        </div>
        <b-button variant="outline-primary" class="w-100 color-primary mb-2" @click="filterModalVisible = false" v-else size="sm">
          <b class="ml-2">See Results</b>
        </b-button>
        <b-button variant="outline-default" class="w-100" @click="clearFilters" size="sm" v-if="(isFiltered || isPreFiltered) && !inquiries.loading" data-cy="reset-filters">
          <b-icon icon="x-circle"></b-icon>
          <b class="ml-2">Reset Filter</b>
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import dayjs from 'dayjs'
import Multiselect from 'vue-multiselect'

// TODO: MxInquiries?
import MxSite from '@/mixins/site'
import { IInquiry, IInquiryFilter, ISearchFilters } from '@/store/inquiry/types'

import LoadingText from '@/components/utility/loading-text.vue'
import { CartItem } from '@/store/cart/types'
import { ResponseArray } from '@/store/types'
import { isMobile, handleResize } from '@/common/helpers/responsive-helper'
import { ResourcePermission } from '@/store/security/types'
import { isCustomerAdminSite } from '@/helpers/sites.helper'
import { blankSearchFilters } from '@/store/inquiry/constants'

const nsInquiries = namespace('inquiries')
const nsInquiryState = namespace('inquiry-state')

// import RequestListRequests from '@/components/request/request-list/requests.vue'
@Component({
  components: {
    'loading-text': LoadingText,
    Multiselect,
  },
})
export default class extends Mixins(MxSite) {
  public statuses: string[] = [
    'Open',
    'Approved',
    'Cancelled',
  ]

  public tabIndex = 0
  public searchTerm = ''
  public activeStatus = 'Open'

  public currentPage = 1

  public filter: IInquiryFilter = {
    InquiryId: '',
    SalesOrderNumber: '',
    CustomerNumber: '',
    Customer: '',
    InquiryDate: '',
  }

  public get activeFilters(): any {
    const filtered = {}
    for (const key in this.filter) {
      if (this.filter[key] !== '') {
        filtered[key] = this.filter[key]
      }
    }
    return filtered
  }

  public async handleSalesRepSelect() {
    this.filterBySearch()
  }

  public sortOptions: { by: string; desc: boolean } = {
    by: 'InquiryDate',
    desc: true,
  }

  public isPreFiltered = false

  private suppressListFetchFromWatchers = false

  @nsInquiries.Action('getInquiries')
  private getInquiries: any

  @nsInquiries.Action('fetchInquiry')
  private fetchInquiry: any

  @nsInquiryState.Action('resetInquiry')
  private resetInquiry: any

  @nsInquiries.State('inquiries')
  public inquiries!: ResponseArray<IInquiry>

  @nsInquiries.State('searchFilters')
  public searchFilters!: ISearchFilters

  @nsInquiries.Action('resetSearchFilters')
  private resetSearchFilters: any

  public isMobile = isMobile()

  public created() {
    this.suppressListFetchFromWatchers = true
    const remembered = (this.$store.state as any).inquiries.rememberedInquiryListTab as string
    if (remembered && this.statuses.includes(remembered)) {
      this.activeStatus = remembered
      this.tabIndex = this.statuses.indexOf(remembered)
    }
    this.initializeSearch()
    this.$nextTick(() => {
      this.suppressListFetchFromWatchers = false
    })
    handleResize((isMobile) => {
      this.isMobile = isMobile
    })
  }

  public onNewInquiry() {
    this.$router.push({
      name: 'new-inquiry',
    })
  }

  public openCrossReferencePdf() {
    const url = '/d/Help_Files/Cross_Ref.pdf'
    window.open(url, '_blank')
  }

  public filterModalVisible = false

  public selectedSalesRep: ResourcePermission | null = null

  // new method to filter inquiries by status
  public rememberInquiryListTabForBack() {
    this.$store.commit('inquiries/inquiryListTabRemember', this.activeStatus)
  }

  public onInquiryStatusTabInput(index: number) {
    const status = this.statuses[index]
    if (!status) {
      return
    }
    this.activeStatus = status
    this.$store.commit('inquiries/inquiryListTabRemember', status)
  }

  public sort(field: string) {
    if (this.sortOptions.by === field) {
      this.sortOptions = {
        ...this.sortOptions,
        desc: !this.sortOptions.desc,
      }
    } else {
      this.sortOptions = {
        by: field,
        desc: true,
      }
    }
  }

  public initializeSearch() {
    if (this.isObjectPrefiltered(this.searchFilters)) {
      this.isPreFiltered = true
    }

    this.filter = {
      ...this.filter,
      InquiryId: this.searchFilters.inquiryId,
      SalesOrderNumber: this.searchFilters.salesOrderNumber,
      CustomerNumber: this.searchFilters.customerNumber,
      Customer: this.searchFilters.customerName,
      InquiryDate: this.searchFilters.inquiryDate,
    }

    this.sortOptions = {
      by: this.searchFilters.sortBy,
      desc: this.searchFilters.sortDesc,
    }

    this.searchTerm = this.searchFilters.keyTerm

    this.getInquiries({
      ...this.searchFilters,
      inquiryStatus: this.activeStatus,
    })
  }

  @Watch('sortOptions')
  @Watch('activeStatus')
  @Watch('filter', { deep: true })
  public filterBySearch() {
    if (this.suppressListFetchFromWatchers) {
      return
    }
    this.getInquiries({
      page: 1,
      sortBy: this.sortOptions.by,
      sortDesc: this.sortOptions.desc,
      salesOrderNumber: this.filter.SalesOrderNumber,
      customerName: this.filter.Customer,
      inquiryDate: this.filter.InquiryDate,
      salesRep: this.selectedSalesRep?.Id ?? '',
      inquiryId: this.filter.InquiryId,
      keyTerm: this.searchTerm,
      inquiryStatus: this.activeStatus,
    })
  }

  @Watch('searchTerm')
  public filterBySearchTerm() {
    if (this.suppressListFetchFromWatchers) {
      return
    }
    if (this.searchTerm.length >= 1 || this.searchTerm.length === 0) {
      this.getInquiries({
        page: 1,
        sortBy: this.sortOptions.by,
        sortDesc: this.sortOptions.desc,
        salesOrderNumber: this.filter.SalesOrderNumber,
        customerName: this.filter.Customer,
        inquiryDate: this.filter.InquiryDate,
        salesRep: this.selectedSalesRep?.Id ?? '',
        inquiryId: this.filter.InquiryId,
        keyTerm: this.searchTerm,
        inquiryStatus: this.activeStatus,
      })
    }
  }

  public get isFiltered(): boolean {
    for (const key in this.filter) {
      if (
        Object.prototype.hasOwnProperty.call(this.filter, key) &&
        this.filter[key] !== '' && this.filter[key] !== 0
      ) {
        return true // At least one field is not empty
      }
    }
    return false // All fields are empty
  }

  public isObjectPrefiltered(filters) {
    for (const key in filters) {
      if (
        Object.prototype.hasOwnProperty.call(filters, key) && filters[key] &&
        filters[key] !== blankSearchFilters[key]
      ) {
        return true // At least one field is not empty
      }
    }
    return false // All fields are empty
  }

  public clearFilters() {
    this.filter = {
      InquiryId: '',
      SalesOrderNumber: '',
      CustomerNumber: '',
      Customer: '',
      InquiryDate: '',
    }

    this.sortOptions = {
      by: 'InquiryDate',
      desc: true,
    }

    this.searchTerm = ''
    this.tabIndex = 0
    this.activeStatus = 'Open'
    this.$store.commit('inquiries/inquiryListTabRemember', 'Open')
    this.resetSearchFilters()
    this.isPreFiltered = false
  }

  public selectAll = false

  public selectedRows: IInquiry[] = []
  public items: IInquiry[] = []

  public get selectedItems(): IInquiry[] {
    return this.inquiries.List.filter((item) => this.selectedRows.includes(item))
  }

  public updateCheckBox(item: IInquiry) {
    const index = this.selectedRows.findIndex(
      (x) => x.InquiryId === item.InquiryId,
    )
    if (index >= 0) {
      this.selectedRows.splice(index, 1)
    } else {
      this.selectedRows.push(item)
    }
  }

  public selectAllItems(event: any) {
    this.selectAll = event.target.checked
    if (this.selectAll) {
      this.selectedRows = [...this.inquiries.List]
    } else {
      this.selectedRows = []
    }
  }

  public formatPrice = (value) => {
    return (
      '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })
    )
  }

  public pageChanged(page: number) {
    this.getInquiries({
      page,
      sortBy: this.sortOptions.by,
      sortDesc: this.sortOptions.desc,
      salesOrderNumber: this.filter.SalesOrderNumber,
      customerName: this.filter.Customer,
      inquiryDate: this.filter.InquiryDate,
      salesRep: this.selectedSalesRep?.Id ?? '',
      inquiryId: this.filter.InquiryId,
      keyTerm: this.searchTerm,
      inquiryStatus: this.activeStatus,
    })
  }

  public loadInquiry(inquiryId: number) {
    this.$store.commit('inquiries/inquiryListTabRemember', this.activeStatus)
    this.fetchInquiry(inquiryId)
    this.$router.push({
      name: 'inquiry_summary',
      params: { inquiryId: inquiryId + '' },
    })
  }

  public calculaturePrice(items: CartItem[]) {
    return items.reduce((total, item) => total + item.quantity * item.price, 0)
  }

  public formatDate(date) {
    return dayjs(date).format('YYYY-MM-DD')
  }

  public fields = [
    {
      key: 'details',
      label: 'Toggle Details',
      thClass: 'responsive-col-hide text-left',
      tdClass: 'responsive-col-hide text-left',
      thStyle: { width: '10%' },
    },
    {
      key: 'InquiryId',
      label: this.isMobile ? 'Inquiry No' : 'Inquiry Number',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: { width: '10%' },
      isSortable: true,
      isFilterable: true,
      hideInlineFilter: true,
    },
    {
      key: 'InquiryDate',
      label: this.isMobile ? 'Date' : 'Inquiry Date',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: { width: '10%' },
      formatter: (val: string) => dayjs(val).format('YYYY-MM-DD'),
      isSortable: true,
      isFilterable: true,
      isDate: true,
      hideInlineFilter: true,
    },
    {
      key: 'Customer',
      label: 'Customer',
      thClass: 'responsive-col text-left',
      tdClass: 'responsive-col text-left',
      thStyle: { width: '15%' },
      isSortable: true,
      isFilterable: true,
    },
    {
      key: 'Material',
      label: 'Material / Description',
      thClass: 'responsive-col text-left',
      tdClass: 'responsive-col text-left',
      thStyle: { width: '30%' },
    },
    {
      key: 'Total',
      label: 'Total Price',
      thClass: 'responsive-col text-right',
      tdClass: 'responsive-col text-right',
      thStyle: { width: '10%' },
      formatter: this.formatPrice,
      isSortable: false,
      isFilterable: false,
    },
  ]

  public customLabel({
    Id,
    ResourceName,
  }: {
    Id: string
    ResourceName: string
  }) {
    return Id ? `${ResourceName} - ${Id}` : ResourceName
  }

  public _isCustomerAdminSite() {
    return isCustomerAdminSite(this.role)
  }
}
</script>
<style>
/* Add custom styles for b-tab */
.nav-link.active {
  position: relative;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #007bff;
}

.header-icon {
  font-size: 18px;
  /* Adjust the size as needed */
  color: #0b67b2 !important;
  cursor: pointer;
}

.clear-btn:hover {
  font-size: 18px;
  /* Adjust the size as needed */
  background-color: lightcoral !important;
  cursor: pointer;
  color: white;
}

.date-picker-btn {
  background: transparent;
}

.filter-tabs {
  flex-wrap: nowrap !important;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 1.5px;
  flex-direction: row !important;
}

.reset-filter-icon {
  display: none !important;
}

@media (max-width: 767px) {
  .new-inquiry-text {
    display: none;
  }

  .actions-bar {
    padding: 5px !important;
  }

  .inquiry-link {
    font-size: 1em !important;
    padding-left: 0 !important;
  }

  .reset-filter-icon {
    display: block !important;
  }
}

.row-details-label {
  color: #000;
  font-weight: 500;
  text-align: right;
}

.th-custom-class {
  position: sticky;
  top: 0px;
  z-index: 2;
}
</style>
